<template>
  <VueDatePicker
    v-model="date"
    :time-config="{ enableTimePicker: enableTimePicker }"
    :hide-input-icon="true"
    @update:model-value="(value: Date) => $emit('update:modelValue', value)"
    :locale="dateFnsLocale"
    :formats="{ preview: format, input: format }"
    :clearable="false"
    :min-date="minDate ?? new Date()"
    :max-date="maxDate"
    teleport="#menu-container"
    :disabled-week-days="disabledWeekDays"
    centered
  >
    <template #dp-input="{ value, isMenuOpen }">
      <slot :value="value" :is-menu-open="isMenuOpen">
        <BaseInputField
          type="text"
          class="bg-inherit"
          :label="label ?? ''"
          :model-value="value"
          :readonly="true"
          :manual-focused="isMenuOpen"
          :required="required"
          :hide-decorators="hideDecorators"
        />
      </slot>
    </template>
    <template #action-row="{ selectDate, closePicker }">
      <div class="flex w-full flex-row justify-end">
        <BaseTextButton color="text" @click="closePicker">{{ $t('common.cancel') }}</BaseTextButton>
        <BaseTextButton color="text" @click="selectDate">{{ $t('common.ok') }}</BaseTextButton>
      </div>
    </template>
    <template #calendar-header="{ day }">
      <div class="flex h-full flex-row items-center justify-center font-normal">
        {{ day[0] }}
      </div>
    </template>
    <template #clear-icon> </template>
  </VueDatePicker>
</template>

<script setup lang="ts">
  import { VueDatePicker } from '@vuepic/vue-datepicker'
  import { de, enUS, fr } from 'date-fns/locale'

  import '@vuepic/vue-datepicker/dist/main.css'
  import BaseInputField from '../form-fields/BaseInputField.vue'
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import BaseTextButton from '../button/text-button/BaseTextButton.vue'

  const localeMap: Record<string, typeof de> = {
    de,
    fr,
    en: enUS,
    ' en': enUS,
  }

  interface Props {
    enableTimePicker?: boolean
    label?: string
    required?: boolean
    disabledWeekDays?: number[]
    minDate?: Date
    maxDate?: Date
    hideDecorators?: boolean
  }

  defineProps<Props>()

  const { locale } = useI18n()

  const dateFnsLocale = computed(
    () => localeMap[locale.value.trim()] ?? localeMap[locale.value] ?? enUS,
  )

  const date = defineModel<Date | undefined>({
    required: true,
  })

  const format = (date: Date) => {
    const currentYear = new Date().getFullYear()
    const day = date.getDate()
    const month = date.toLocaleString(locale.value, { month: 'long' })
    const dayName = date.toLocaleString(locale.value, { weekday: 'long' })
    const year =
      date.getFullYear() != currentYear ? ' ' + date.getFullYear().toString().slice(2, 4) : ''

    return `${dayName}, ${day}. ${month}${year}`
  }
</script>

<style>
  .dp__theme_light {
    --dp-background-color: var(--color-surface-container-high);
    --dp-text-color: var(--color-on-surface);
    --dp-hover-color: var(--color-primary-container);
    --dp-hover-text-color: var(--color-on-primary-container);
    --dp-hover-icon-color: #959595;
    --dp-primary-color: var(--color-primary);
    --dp-primary-disabled-color: #6bacea;
    --dp-primary-text-color: var(--color-on-primary);
    --dp-secondary-color: var(--color-outline-variant);
    --dp-border-color: var(--color-surface-container-high);
    --dp-menu-border-color: #f9f1ff;
    --dp-border-color-hover: #aaaeb7;
    --dp-border-color-focus: #aaaeb7;
    --dp-disabled-color: #f6f6f6;
    --dp-scroll-bar-background: #f3f3f3;
    --dp-scroll-bar-color: #959595;
    --dp-success-color: #76d275;
    --dp-success-color-disabled: #a3d9b1;
    --dp-icon-color: #959595;
    --dp-danger-color: #ff6f60;
    --dp-marker-color: #ff6f60;
    --dp-tooltip-color: #fafafa;
    --dp-disabled-color-text: #8e8e8e;
    --dp-highlight-color: rgb(25 118 210 / 10%);
    --dp-range-between-dates-background-color: var(--dp-hover-color);
    --dp-range-between-dates-text-color: var(--dp-primary-color);
    --dp-range-between-border-color: rgb(238, 225, 250);
  }

  :root {
    /*General*/
    --dp-font-family: 'Work Sans';
    --dp-border-radius: var(--radius-large);
    --dp-cell-border-radius: var(--radius-full); /*Specific border radius for the calendar cell*/
    --dp-common-transition: all 0.1s ease-in; /*Generic transition applied on buttons and calendar cells*/

    /*Sizing*/
    --dp-button-height: 35px; /*Size for buttons in overlays*/
    --dp-month-year-row-height: 35px; /*Height of the month-year select row*/
    --dp-month-year-row-button-size: 35px; /*Specific height for the next/previous buttons*/
    --dp-button-icon-height: 20px; /*Icon sizing in buttons*/
    --dp-cell-size: 40px; /*Width and height of calendar cell*/
    --dp-cell-padding: 5px; /*Padding in the cell*/
    --dp-common-padding: 10px; /*Common padding used*/
    --dp-menu-min-width: 360px; /*Adjust the min width of the menu*/
    --dp-action-buttons-padding: 2px 5px; /*Adjust padding for the action buttons in action row*/
    --dp-row-margin: 5px 0; /*Adjust the spacing between rows in the calendar*/
    --dp-calendar-header-cell-padding: 0.5rem; /*Adjust padding in calendar header cells*/
    --dp-two-calendars-spacing: 10px; /*Space between multiple calendars*/
    --dp-overlay-col-padding: 3px; /*Padding in the overlay column*/
    --dp-time-inc-dec-button-size: 32px; /*Sizing for arrow buttons in the time picker*/
    --dp-menu-padding: 6px 8px; /*Menu padding*/

    /*Font sizes*/
    --dp-font-size: var(--md-sys-typescale-body-large-font-size); /*Default font-size*/

    /*Transitions*/
    --dp-animation-duration: 0.1s; /*Transition duration*/
    --dp-menu-appear-transition-timing: cubic-bezier(
      0.4,
      0,
      1,
      1
    ); /*Timing on menu appear animation*/
    --dp-transition-timing: ease-out; /*Timing on slide animations*/
  }
  .dp__input_wrap,
  .dp__main > div {
    background-color: inherit;
  }
</style>
